import React, { useMemo } from 'react';
import {
  PERMISSION_MODULE_GROUPS,
  PERMISSION_ACTIONS,
  ROLE_DEFAULT_PERMISSIONS,
} from '../utils/permissions';

/**
 * Live preview of what a user with the given (role, permissions) will see
 * after login — visible sidebar menus and per-module action capabilities.
 *
 * Mirrors `hasPermission(user, module, action)` from utils/permissions but
 * operates on the in-memory matrix being edited, so the admin sees changes
 * instantly as they tick.
 *
 * Props:
 *  - role       — user role string (e.g., 'employee', 'sales', 'admin')
 *  - matrix     — { [moduleKey]: { view, create, edit, delete } }
 */
export default function PermissionsPreview({ role, matrix }) {
  // Mirror the hasPermission resolver: admin/super_admin → full access; else
  // use the live matrix (if non-empty), otherwise fall back to role defaults.
  const resolved = useMemo(() => {
    if (role === 'admin' || role === 'super_admin') {
      const out = {};
      for (const g of PERMISSION_MODULE_GROUPS) {
        for (const m of g.modules) {
          out[m.key] = { view: true, create: true, edit: true, delete: true };
        }
      }
      return out;
    }
    const hasMatrix = matrix && Object.values(matrix).some((v) =>
      v && (v.view || v.create || v.edit || v.delete)
    );
    const fallback = ROLE_DEFAULT_PERMISSIONS[role] || {};
    const source = hasMatrix ? matrix : fallback;
    const out = {};
    for (const g of PERMISSION_MODULE_GROUPS) {
      for (const m of g.modules) {
        const cell = source?.[m.key] || {};
        out[m.key] = {
          view: !!cell.view,
          create: !!cell.create,
          edit: !!cell.edit,
          delete: !!cell.delete,
        };
      }
    }
    return out;
  }, [role, matrix]);

  const visibleModules = useMemo(() => {
    const items = [];
    for (const g of PERMISSION_MODULE_GROUPS) {
      const allowed = g.modules.filter((m) => resolved[m.key]?.view);
      if (allowed.length) items.push({ group: g.label, modules: allowed });
    }
    return items;
  }, [resolved]);

  // Module → list of allowed action keys (excluding view, since view is implicit).
  const actionMap = useMemo(() => {
    const out = [];
    for (const g of PERMISSION_MODULE_GROUPS) {
      for (const m of g.modules) {
        const cell = resolved[m.key] || {};
        if (!cell.view) continue;
        const actions = PERMISSION_ACTIONS.filter((a) => a.key !== 'view' && cell[a.key]);
        out.push({ module: m, actions });
      }
    }
    return out;
  }, [resolved]);

  const totalVisible = visibleModules.reduce((n, g) => n + g.modules.length, 0);

  return (
    <div
      className="border-2 border-blue-200 bg-blue-50/50 rounded-lg p-4 mt-3"
      data-testid="permissions-preview"
    >
      <div className="flex items-center justify-between mb-3">
        <h4 className="font-semibold text-blue-900 inline-flex items-center gap-2">
          👁️ Preview — What this user will see after login
        </h4>
        <span
          className="text-xs px-2 py-0.5 rounded bg-blue-100 text-blue-800 font-mono"
          data-testid="permissions-preview-count"
        >
          {totalVisible} menu item{totalVisible === 1 ? '' : 's'}
        </span>
      </div>

      {totalVisible === 0 ? (
        <div className="p-3 rounded bg-amber-50 border border-amber-300 text-sm text-amber-900">
          ⚠️ No menus visible. This user will not be able to use the app — tick at
          least one module's View permission.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Visible menus */}
          <div>
            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
              📋 Sidebar Menus
            </div>
            <div className="space-y-2">
              {visibleModules.map((g) => (
                <div key={g.group}>
                  <div className="text-[11px] text-gray-500 uppercase">{g.group}</div>
                  <div className="flex flex-wrap gap-1.5 mt-1">
                    {g.modules.map((m) => (
                      <span
                        key={m.key}
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-white border border-blue-300 text-xs text-blue-900"
                        data-testid={`preview-menu-${m.key}`}
                      >
                        <span>{m.icon}</span>
                        <span>{m.label}</span>
                      </span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Per-module action capabilities */}
          <div>
            <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-1.5">
              ⚡ Actions Allowed
            </div>
            <div className="space-y-1 max-h-64 overflow-y-auto pr-1">
              {actionMap.map(({ module, actions }) => (
                <div
                  key={module.key}
                  className="flex items-center gap-2 text-xs"
                  data-testid={`preview-actions-${module.key}`}
                >
                  <span className="w-44 text-gray-700 truncate">
                    {module.icon} {module.label}
                  </span>
                  {actions.length === 0 ? (
                    <span className="text-gray-400 italic">view only</span>
                  ) : (
                    <span className="flex flex-wrap gap-1">
                      {actions.map((a) => (
                        <span
                          key={a.key}
                          className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${
                            a.key === 'delete'
                              ? 'bg-red-100 text-red-800'
                              : a.key === 'create'
                              ? 'bg-emerald-100 text-emerald-800'
                              : 'bg-amber-100 text-amber-800'
                          }`}
                        >
                          {a.label}
                        </span>
                      ))}
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
